import { useState } from "react";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { BACKGROUNDS, CONTACT, whatsappLink } from "@/data/content";
import { PdpaNotice } from "@/components/site/PdpaNotice";
import { getReferrer, getReferrerName } from "@/lib/referral";
import { metaCookies, newEventId, pixel } from "@/lib/pixel";

export const LeadForm = ({ quizAnswers = [], fitScore = null, source = "website", dark = false, compact = false, onSuccess }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", background: "", message: "", consent: false });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const fieldClass = `field ${dark ? "field-dark" : ""}`;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) { setError("Please acknowledge the PDPA consent to continue."); return; }
    setStatus("loading");
    const eventId = newEventId("lead");
    try {
      await api.post("/leads", { ...form, email: form.email || null, background: form.background || null, message: form.message || null, quiz_answers: quizAnswers, fit_score: fitScore, source, referrer: getReferrer(), event_id: eventId, ...metaCookies() });
      setStatus("success");
      pixel("Lead", { content_name: "Career enquiry", content_category: source, ...(fitScore != null ? { value: fitScore, currency: "SGD" } : {}) }, { eventId });
      pixel("SubmitApplication", { content_name: "Career enquiry", content_category: source }, { eventId });
      onSuccess?.();
    } catch (err) {
      setStatus("idle");
      setError(formatApiError(err.response?.data?.detail, err.message));
    }
  };

  if (status === "success") {
    return (
      <div data-testid="lead-form-success" className="grid gap-5">
        <span className="grid place-items-center w-14 h-14 rounded-full bg-[var(--gold)] text-[var(--navy)]"><Check size={26} /></span>
        <h3 className="font-serif text-3xl leading-tight m-0">Thank you, {form.name.split(" ")[0]}. We'll be in touch shortly.</h3>
        <p className={`m-0 text-sm leading-relaxed ${dark ? "text-[rgba(244,241,234,.6)]" : "text-[var(--muted)]"}`}>Prefer to move faster? Message us now on WhatsApp.</p>
        <div className="flex flex-wrap gap-3">
          <a data-testid="success-whatsapp-link" className="btn btn-gold btn-sm" href={whatsappLink(`Hi Affinity Group, I'm ${form.name}. I've just submitted a career enquiry on your website.`)} target="_blank" rel="noopener noreferrer"><MessageCircle size={15} /> WhatsApp {CONTACT.whatsappDisplay}</a>
        </div>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={submit} className="grid gap-4">
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <input data-testid="lead-name-input" className={fieldClass} placeholder="Full name *" required minLength={2} value={form.name} onChange={set("name")} />
        <input data-testid="lead-phone-input" className={fieldClass} placeholder="WhatsApp / mobile number *" required minLength={6} value={form.phone} onChange={set("phone")} />
      </div>
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <input data-testid="lead-email-input" className={fieldClass} type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} />
        <select data-testid="lead-background-select" className={fieldClass} value={form.background} onChange={set("background")}>
          <option value="">Your background</option>
          {BACKGROUNDS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      {!compact && <textarea data-testid="lead-message-input" className={fieldClass} placeholder="Anything you'd like us to know before we speak? (optional)" value={form.message} onChange={set("message")} />}
      <label className={`flex gap-3 items-start text-[11px] leading-relaxed cursor-pointer ${dark ? "text-[rgba(244,241,234,.5)]" : "text-[var(--muted)]"}`}>
        <input data-testid="lead-consent-checkbox" type="checkbox" className="mt-1 w-4 h-4 accent-[var(--gold)] flex-none" checked={form.consent} onChange={set("consent")} />
        <PdpaNotice dark={dark} testId="lead-pdpa" />
      </label>
      {error && <p data-testid="lead-form-error" role="alert" className="m-0 text-sm text-[#d9534f]">{error}</p>}
      {getReferrer() && <p data-testid="lead-form-referrer" className={`m-0 text-[12px] ${dark ? "text-[rgba(244,241,234,.55)]" : "text-[var(--muted)]"}`}>Invited by <strong className="text-[#9a7225]">{getReferrerName()}</strong>. We will let them know you reached out.</p>}
      <button data-testid="lead-submit-button" type="submit" disabled={status === "loading"} className="btn btn-gold w-full sm:w-auto justify-self-start disabled:opacity-60">
        {status === "loading" ? "Sending…" : "Request a private conversation"} <ArrowRight size={16} />
      </button>
    </form>
  );
};
